import { createContext, ReactNode, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Snackbar, SnackbarCloseReason } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { createErrorToast, createInfoToast, createSuccessToast, createToast, createWarningToast } from './toastService';
import { ToastApi, ToastId, ToastInput, ToastOptions, ToastState } from './toastTypes';

const MAX_VISIBLE_TOASTS = 4;
const TOAST_GAP = 12;
const TOAST_OFFSET = 70;
const TOAST_HEIGHT = 72;
const SCROLL_DISMISSIBLE_SEVERITY: ToastState['severity'] = 'success';
const SCROLL_DISMISS_GRACE_PERIOD = 300;
const TOAST_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;

const ToastContext = createContext<ToastApi | null>(null);

const TOAST_ALERT_SX: SxProps<Theme> = {
  minWidth: 320,
  maxWidth: 520,
  alignItems: 'center',
  boxShadow: theme => `0 5px 5px ${theme.palette.common.shadowDefault || theme.palette.divider}`,
};

const getToastOffsetSx = (index: number): SxProps<Theme> => ({
  top: `${TOAST_OFFSET + index * (TOAST_HEIGHT + TOAST_GAP)}px !important`,
  right: '-10px',
});

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const closeToast = useCallback((toastId: ToastId) => {
    setToasts(currentToasts => currentToasts.filter(toastItem => toastItem.id !== toastId));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts(currentToasts => {
      currentToasts.forEach(toastItem => toastItem.onClose?.());
      return [];
    });
  }, []);

  const dismissScrollableToasts = useCallback(() => {
    const now = Date.now();

    setToasts(currentToasts =>
      currentToasts.filter(toastItem => {
        const isScrollDismissible =
          toastItem.severity === SCROLL_DISMISSIBLE_SEVERITY &&
          now - toastItem.createdAt >= SCROLL_DISMISS_GRACE_PERIOD;

        if (!isScrollDismissible) {
          return true;
        }

        toastItem.onClose?.();
        return false;
      }),
    );
  }, []);

  const enqueueToast = useCallback((toastItem: ToastState) => {
    setToasts(currentToasts =>
      [toastItem, ...currentToasts.filter(currentToast => currentToast.id !== toastItem.id)].slice(
        0,
        MAX_VISIBLE_TOASTS,
      ),
    );

    return toastItem.id;
  }, []);

  const hasScrollDismissibleToasts = toasts.some(toastItem => toastItem.severity === SCROLL_DISMISSIBLE_SEVERITY);

  useEffect(() => {
    if (!hasScrollDismissibleToasts) {
      return undefined;
    }

    const handleScroll = () => dismissScrollableToasts();

    window.addEventListener('scroll', handleScroll, true);

    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [hasScrollDismissibleToasts, dismissScrollableToasts]);

  const toastApi = useMemo(
    () => ({
      show: (toastInput: ToastInput) => enqueueToast(createToast(toastInput)),
      success: (message: ToastOptions['message'], options?: Omit<ToastOptions, 'message' | 'severity'>) =>
        enqueueToast(createSuccessToast(message, options)),
      error: (message: ToastOptions['message'], options?: Omit<ToastOptions, 'message' | 'severity'>) =>
        enqueueToast(createErrorToast(message, options)),
      warning: (message: ToastOptions['message'], options?: Omit<ToastOptions, 'message' | 'severity'>) =>
        enqueueToast(createWarningToast(message, options)),
      info: (message: ToastOptions['message'], options?: Omit<ToastOptions, 'message' | 'severity'>) =>
        enqueueToast(createInfoToast(message, options)),
      close: closeToast,
      clear: clearToasts,
    }),
    [clearToasts, closeToast, enqueueToast],
  );

  const handleClose = (toastItem: ToastState) => (_event: Event | SyntheticEvent, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    toastItem.onClose?.();
    closeToast(toastItem.id);
  };

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      {toasts.map((toastItem, index) => (
        <Snackbar
          key={toastItem.id}
          open
          anchorOrigin={TOAST_ANCHOR_ORIGIN}
          autoHideDuration={toastItem.autoHideDuration}
          onClose={handleClose(toastItem)}
          sx={getToastOffsetSx(index)}
        >
          <Alert
            severity={toastItem.severity}
            action={toastItem.action}
            onClose={handleClose(toastItem)}
            sx={TOAST_ALERT_SX}
          >
            {toastItem.title ? <AlertTitle>{toastItem.title}</AlertTitle> : null}
            {toastItem.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const toastApi = useContext(ToastContext);

  if (!toastApi) {
    throw new Error('useToast must be used inside ToastProvider');
  }

  return toastApi;
};
